import { getPath, PathUnit } from '@/app/consts/path.const';
import { getRolesByPermission, RoleEnum } from '@/app/consts/role.const';
import { environment } from '@/environments/environment';
import { Permission } from './permissions.const';

/** Prototype pages stay available on local (`ng serve`) and the development build only. */
export const eventObservationsEnabled =
  environment.environmentName === 'development' || environment.environmentName === 'local';

export interface AppLink {
  label: string;
  translationKey: string;
  icon?: string;
  customSvg?: string;
  route?: string;
  children?: AppLink[];
  roles: RoleEnum[];
  isQuickLink?: boolean;
  isHideInSideNav?: boolean;
  roleAuthGuard?: boolean; // default true
}

export const AllAppLinks: AppLink[] = [
  { 
    label: 'Dashboard', 
    translationKey: 'nav.dashboard',
    icon: 'dashboard', 
    route: getPath(PathUnit.Dashboard), 
    roles: getRolesByPermission([]), 
    roleAuthGuard: false,
  },
  {
    label: 'Inspection Report Creation',
    translationKey: 'nav.inspectionreportcreation',
    icon: 'clipboard-check',
    route: getPath(PathUnit.InspectionReportCreate),
    roles: getRolesByPermission([Permission.INSPECTION_CREATE]),
    isHideInSideNav: true,
  },
  {
    label: 'Deviation Report Creation',
    translationKey: 'nav.deviationreportcreation',
    icon: 'warning',
    route: getPath(PathUnit.DeviationReportCreate),
    roles: Array.from(new Set([...getRolesByPermission([Permission.DEVIATION_CREATE_PRODUCT]), ...getRolesByPermission([Permission.DEVIATION_CREATE_OPERATION])])),
    isHideInSideNav: true,
  },
  {
    label: 'Deviation Voice Report List',
    translationKey: 'nav.deviationvoicereport',
    icon: 'mic',
    route: getPath(PathUnit.DeviationVoiceReport),
    roles: [RoleEnum.Engineer, RoleEnum.Driver],
    isHideInSideNav: true,
  },
  {
    label: 'Deviation Voice Report Detail',
    translationKey: 'nav.deviationvoicereportdetail',
    icon: 'mic',
    route: getPath(PathUnit.DeviationVoiceReportDetail),
    roles: [RoleEnum.Engineer, RoleEnum.Driver],
    isHideInSideNav: true,
  },
  {
    label: 'Event Observations',
    translationKey: 'nav.eventobservations',
    icon: 'warning',
    route: getPath(PathUnit.EventObservations),
    roles: [RoleEnum.Admin, RoleEnum.Lead, RoleEnum.Engineer, RoleEnum.Driver],
    isHideInSideNav: !eventObservationsEnabled,
  },
  {
    label: 'iOS Voice Capture',
    translationKey: 'nav.eventobservationmobile',
    icon: 'mic',
    route: getPath(PathUnit.EventObservationMobile),
    roles: [RoleEnum.Engineer, RoleEnum.Driver],
    isHideInSideNav: !eventObservationsEnabled,
  },
  {
    label: 'Deviation Report Studio',
    translationKey: 'nav.deviationreportstudio',
    icon: 'edit',
    route: getPath(PathUnit.DeviationReportStudio),
    roles: [RoleEnum.Driver],
    isHideInSideNav: true,
  },
  {
    label: 'Deviation Report Detail',
    translationKey: 'nav.deviationreportdetail',
    icon: 'warning',
    route: getPath(PathUnit.DeviationReportDetail),
    roles: getRolesByPermission([Permission.DEVIATION_READ]),
    isHideInSideNav: true,
  },
  {
    label: 'Deviation Report Edit',
    translationKey: 'nav.deviationreportedit',
    icon: 'pen',
    route: getPath(PathUnit.DeviationReportEdit),
    roles: getRolesByPermission([Permission.DEVIATION_UPDATE]),
    isHideInSideNav: true,
  },
  {
    label: 'Deviation Detail',
    translationKey: 'nav.deviationdetail',
    icon: 'warning',
    route: getPath(PathUnit.DeviationReportDetail),
    roles: getRolesByPermission([Permission.DEVIATION_READ]),
    isHideInSideNav: true,
  },
  {
    label: 'Inspection Report Detail',
    translationKey: 'nav.inspectionreportdetail',
    icon: 'insights',
    route: getPath(PathUnit.InspectionReportDetail),
    roles: getRolesByPermission([Permission.INSPECTION_LIST_FLEET_REPORTS, Permission.INSPECTION_READ]),
    isHideInSideNav: true,
  },
  {
    label: 'Inspection Report Edit',
    translationKey: 'nav.inspectionreportedit',
    icon: 'pen',
    route: getPath(PathUnit.InspectionReportEdit),
    roles: getRolesByPermission([Permission.INSPECTION_UPDATE]),
    isHideInSideNav: true,
  },
  { 
    label: 'Fleet Management', 
    translationKey: 'nav.fleetmanagement',
    icon: 'truck',
    roles: getRolesByPermission([Permission.FLEET_LIST_ALL]), 
    children: [
      {
        label: 'Vehicle Management',
        translationKey: 'nav.vehicleManagement',
        icon: 'truck',
        customSvg: 'M14 18V6a1 1 0 0 0-1-1H2a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1h1M14 9h4l4 4v4a1 1 0 0 1-1 1h-2M9 18h6M9 18a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM18 18a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM3 13h8M14 13h6',
        route: getPath(PathUnit.FleetManagement),
        roles: getRolesByPermission([Permission.FLEET_LIST_ALL]),
      },
      {
        label: 'Vehicle Check-ins',
        translationKey: 'nav.vehicleCheckins',
        icon: 'tick',
        customSvg: 'M2 16V7a1 1 0 0 1 1-1h9v10H2Z M12 16V8.3c0-.5.3-.8.8-.8H16l4.2 4.2c.2.2.4.5.4.8V16H12Z M5.2 16.5a1.8 1.8 0 1 1 3.6 0 1.8 1.8 0 1 1-3.6 0Z M16.2 16.5a1.8 1.8 0 1 1 3.6 0 1.8 1.8 0 1 1-3.6 0Z M16.5 4.5a3 3 0 1 1 6 0 3 3 0 1 1-6 0Z M17.9 4.6l1.3 1.3 2.3-2.4',
        route: getPath(PathUnit.VehicleCheckIns),
        roles: [RoleEnum.Admin, RoleEnum.Lead],
      },
    ],
  },
  {
    label: 'Reports Manage',
    translationKey: 'nav.reportsmanage',
    icon: 'text',
    route: getPath(PathUnit.ReportsManage),
    roles: getRolesByPermission([Permission.REPORTS_MANAGE]),
  },
  {
    label: 'Fleet Management Creation',
    translationKey: 'nav.fleetmanagementcreation',
    icon: 'truck',
    route: getPath(PathUnit.FleetManagementCreate),
    roles: getRolesByPermission([Permission.FLEET_CREATE]),
    isHideInSideNav: true,
  },
  {
    label: 'Fleet Management Edit',
    translationKey: 'nav.fleetmanagementedit',
    icon: 'truck',
    route: getPath(PathUnit.FleetManagementEdit),
    roles: getRolesByPermission([Permission.FLEET_UPDATE]),
    isHideInSideNav: true,
  },
  {
    label: 'Fleet Management View',
    translationKey: 'nav.fleetmanagementview',
    icon: 'truck',
    route: getPath(PathUnit.FleetManagementView),
    roles: getRolesByPermission([Permission.FLEET_READ]),
    isHideInSideNav: true,
  },
  {
    label: 'Image Upload Test',
    translationKey: 'nav.imageUploadTest',
    icon: 'image',
    route: getPath(PathUnit.ImageUploadTest),
    roles: getRolesByPermission([]),
    isHideInSideNav: true,
    roleAuthGuard: false,
  },
  {
    label: 'History Software Versions',
    translationKey: 'nav.historysoftwareversions',
    icon: 'code',
    route: getPath(PathUnit.HistorySoftwareVersions),
    roles: getRolesByPermission([]),
    isHideInSideNav: true,
    roleAuthGuard: false,
  },
  {
    label: 'UX Demo Portal',
    translationKey: 'nav.uxdemoportal',
    icon: 'grid',
    route: getPath(PathUnit.UXDemo),
    roles: getRolesByPermission([]),
    isHideInSideNav: true,
    roleAuthGuard: false,
  },
];

export function getNavItemsByRole(role: RoleEnum): AppLink[] {
  function filterLinks(links: AppLink[]): AppLink[] {
    return links.reduce<AppLink[]>((acc, link) => {
      const children = link.children ? filterLinks(link.children) : undefined;
      if (link.roles.includes(role) || (children && children.length > 0)) {
        acc.push({ ...link, children });
      }
      return acc;
    }, []);
  }
  return filterLinks(AllAppLinks);
}


export function getQuickLinksByRole(role: RoleEnum): AppLink[] {
  const result: AppLink[] = [];
  function collect(links: AppLink[]) {
    for (const link of links) {
      if (link.isQuickLink && link.roles.includes(role)) {
        result.push(link);
      }
      if (link.children) {
        collect(link.children);
      }
    }
  }
  collect(AllAppLinks);
  return result;
}

export function getRolesByLink(labelOrRoute: string): RoleEnum[] {
  let roles: RoleEnum[] = [];
  function findLink(links: AppLink[]): boolean {
    for (const link of links) {
      if (link.label === labelOrRoute || link.route === labelOrRoute) {
        roles = link.roles;
        return true;
      }
      if (link.children && findLink(link.children)) {
        return true;
      }
    }
    return false;
  }
  findLink(AllAppLinks);
  return roles;
}

/**
 * 检查路由是否需要角色验证（roleAuthGuard !== false）
 */
export function isRouteProtected(route: string): boolean {
  function findLink(links: AppLink[]): AppLink | null {
    for (const link of links) {
      if (link.route === route) {
        return link;
      }
      if (link.children) {
        const childResult = findLink(link.children);
        if (childResult) return childResult;
      }
    }
    return null;
  }
  
  const linkConfig = findLink(AllAppLinks);
  if (!linkConfig) return true; // 默认需要验证
  
  return linkConfig.roleAuthGuard !== false;
}

